import { useState, useCallback } from "react";
import * as Select from "@radix-ui/react-select";
import { ChevronDown, Check, Settings, Info } from "lucide-react";
import SplitTransformationDialog from "./transformations/split";
import MergeTransformationDialog from "./transformations/merge";
import AddPrefixTransformationDialog from "./transformations/prefix";
import AddSuffixTransformationDialog from "./transformations/suffix";
import RemoveSubstringTransformationDialog from "./transformations/substring";
import UppercaseTransformationDialog from "./transformations/uppercase";
import TitleCaseTransformationDialog from "./transformations/titlecase";
import TrimWhitespaceTransformationDialog from "./transformations/trim-whitespace";
import LowercaseTransformationDialog from "./transformations/lowercase";
import FindReplaceTransformationDialog from "./transformations/find-replace";
import { FileSummaryCard } from "./FileSummaryCard";
import { toSnakeCase } from "@shared/utils/caseConverter";

// Confidence Badge Component
const ConfidenceBadge = ({ level }) => {
  // Convert 0-1 float to high/medium/low
  const getConfidenceLevel = (score) => {
    if (typeof score === "number") {
      if (score >= 0.8) return "high";
      if (score >= 0.5) return "medium";
      return "low";
    }
    return score; // Already a string
  };

  const confidenceLevel = getConfidenceLevel(level);

  const configs = {
    high: {
      color: "bg-green-100 text-green-800 border-green-200",
      text: "High Confidence",
      icon: "●",
    },
    medium: {
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      text: "Medium Confidence",
      icon: "●",
    },
    low: {
      color: "bg-red-100 text-red-800 border-red-200",
      text: "Low Confidence",
      icon: "●",
    },
  };

  const config = configs[confidenceLevel] || configs.low;

  return (
    <div
      className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}
    >
      <span className="text-xs">{config.icon}</span>
      <span>{config.text}</span>
    </div>
  );
};

// Transformation Dropdown Component
const TransformationDropdown = ({
  value,
  onChange,
  disabled = false,
  allowedTransformations = ["no_transformation"],
}) => {
  const allTransformationOptions = [
    { value: "no_transformation", label: "No Transformation" },
    { value: "split", label: "Split" },
    { value: "merge", label: "Merge (Concat)" },
    { value: "add_prefix", label: "Add Prefix" },
    { value: "add_suffix", label: "Add Suffix" },
    { value: "remove_substring", label: "Remove Substring" },
    { value: "uppercase", label: "Uppercase" },
    { value: "lowercase", label: "Lowercase" },
    { value: "title_case", label: "Title Case" },
    { value: "trim_whitespace", label: "Trim Whitespace" },
    { value: "find_replace", label: "Find & Replace" },
  ];

  const filteredOptions = allTransformationOptions.filter((option) =>
    allowedTransformations.includes(option.value)
  );

  // Find the current selected option's label
  const selectedOption = filteredOptions.find((opt) => opt.value === value);
  const displayValue = selectedOption
    ? selectedOption.label
    : "Select transformation...";

  return (
    <Select.Root value={value} onValueChange={onChange} disabled={disabled}>
      <Select.Trigger
        className={`w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left cursor-pointer 
  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
  hover:border-gray-400 transition-colors duration-200 flex items-center justify-between ${
    disabled ? "opacity-50 cursor-not-allowed" : ""
  }`}
      >
        <Select.Value placeholder="Select transformation..." />
        <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0 ml-2" />
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className="bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-60 overflow-auto"
          position="popper"
          sideOffset={5}
        >
          <Select.Viewport>
            {filteredOptions.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none relative flex items-center"
              >
                <Select.ItemText>{option.label}</Select.ItemText>
                <Select.ItemIndicator className="absolute right-2">
                  <Check className="h-4 w-4" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

// Source Column Dropdown Component
const SourceColumnDropdown = ({
  value,
  onChange,
  options = [],
  disabled = false,
  sourceAssignments = {}, // ← ADD
  currentTargetField = "", // ← ADD
}) => {
  const enrichedOptions = options
    .filter((sourceCol) => sourceCol && sourceCol.trim() !== "")
    .map((sourceCol) => {
      const assignedTo = Object.entries(sourceAssignments).find(
        ([source, target]) =>
          source === sourceCol && target !== currentTargetField
      );

      return {
        value: sourceCol,
        label: sourceCol,
        isAssigned: !!assignedTo,
        assignedTo: assignedTo?.[1],
      };
    });

  return (
    <Select.Root value={value} onValueChange={onChange} disabled={disabled}>
      <Select.Trigger
        className={`w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors duration-200 flex items-center justify-between ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <Select.Value placeholder="Select source column..." />
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </Select.Trigger>

      <Select.Portal>
        <Select.Content className="bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-60 overflow-auto">
          <Select.Viewport>
            {enrichedOptions.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                  option.isAssigned ? "text-orange-600" : ""
                }`}
              >
                <div className="flex flex-col">
                  <Select.ItemText>{option.label}</Select.ItemText>
                </div>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

// Individual Mapping Row Component - Only this row re-renders when changed
const MappingRow = ({
  targetField,
  isRequired = false,
  transformation = "no_transformation",
  sourceColumn = "",
  confidence = "low",
  fileData = ["Sample 1", "Sample 2..."],
  transformedData = ["Sample 1", "Sample 2..."],
  sampleData = ["Sample 1", "Sample 2..."],
  sourceColumnOptions = [],
  sourceAssignments = {}, // ← ADD this
  allowedTransformations = ["no_transformation"], // ← ADD this
  hasErrors = false, // ← ADD this
  isLoadingPreview = false, // ← ADD this
  onTransformationChange,
  onSourceColumnChange,
  onOpenTransformationModal,
}) => {
  const handleTransformationChange = useCallback(
    (newTransformation) => {
      onTransformationChange?.(targetField, newTransformation);
    },
    [targetField, onTransformationChange]
  );

  const handleSourceColumnChange = useCallback(
    (newSourceColumn) => {
      onSourceColumnChange?.(targetField, newSourceColumn);
    },
    [targetField, onSourceColumnChange]
  );

  return (
    <tr className="hover:bg-gray-50 border-b border-gray-100 transition-colors duration-150">
      {/* Target Field */}
      <td className="px-4 py-3 align-top">
        <div className="flex items-center space-x-2">
          <span className="font-medium text-gray-900">{targetField}</span>
          {isRequired && (
            <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded font-medium">
              Required
            </span>
          )}
        </div>
      </td>

      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <TransformationDropdown
              value={transformation}
              onChange={handleTransformationChange}
              allowedTransformations={allowedTransformations} // ← ADD this
              disabled={!sourceColumn} // ← CHANGE from isSourceDisabled
            />
          </div>
          {transformation && transformation !== "no_transformation" && (
            <button
              onClick={() =>
                onOpenTransformationModal?.(
                  transformation,
                  targetField,
                  fileData
                )
              }
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
              title="Transformation Settings"
            >
              <Settings className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>

      <td className="px-4 py-3 align-top">
        <SourceColumnDropdown
          value={sourceColumn}
          onChange={handleSourceColumnChange}
          options={sourceColumnOptions}
          sourceAssignments={sourceAssignments} // ← ADD this
          currentTargetField={targetField} // ← ADD this
          disabled={transformation !== "no_transformation"} // Keep existing logic
        />
      </td>

      {/* Confidence */}
      <td className="px-4 py-3 align-top">
        <ConfidenceBadge level={confidence} />
      </td>

      {/* File Data */}
      <td className="px-4 py-3 align-top">
        {isLoadingPreview ? (
          <span className="text-gray-400 text-sm">Loading...</span>
        ) : Array.isArray(fileData) && fileData.length > 0 ? (
          <div
            className="text-sm text-gray-600 max-w-xs"
            title={fileData.join(", ")}
          >
            {fileData.slice(0, 2).map((item, idx) => (
              <div key={idx} className="truncate">
                {item}
              </div>
            ))}
            {fileData.length > 2 && (
              <span className="text-gray-400">
                ...+{fileData.length - 2} more
              </span>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </td>

      {/* Transformed Data */}
      <td className="px-4 py-3 align-top">
        {isLoadingPreview ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
            <span className="text-sm text-gray-500">Transforming...</span>
          </div>
        ) : Array.isArray(transformedData) && transformedData.length > 0 ? (
          <div className="space-y-1 text-sm text-gray-900 max-w-xs">
            {transformedData.slice(0, 2).map((item, idx) => (
              <div key={idx} className="truncate" title={item}>
                {item}
              </div>
            ))}
            {transformedData.length > 2 && (
              <span className="text-gray-400">
                ...+{transformedData.length - 2} more
              </span>
            )}
            {hasErrors && (
              <div className="flex items-center space-x-1 text-red-600">
                <span className="text-xs">⚠ Has errors</span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">No transformation</span>
        )}
      </td>

      {/* Sample Data */}
      <td className="px-4 py-3 align-top">
        {Array.isArray(sampleData) && sampleData.length > 0 ? (
          <div className="text-sm text-gray-500 max-w-xs space-y-1">
            {sampleData.slice(0, 2).map((item, idx) => (
              <div key={idx} className="truncate" title={item}>
                {item}
              </div>
            ))}
            {sampleData.length > 2 && (
              <span className="text-gray-400">
                ...+{sampleData.length - 2} more
              </span>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </td>
    </tr>
  );
};

const steps = [
  {
    title: "Map Required Fields",
    description: "Customer ID is mandatory and must be mapped",
  },
  {
    title: "Review Source Column Assignments",
    description: "Ensure each target field maps to the correct source column",
  },
  {
    title: "Check Data Transformations",
    description: "Verify transformations match your data format needs",
  },
  {
    title: "Validate Confidence Levels",
    description: "Address any low confidence mappings (red indicators)",
  },
  {
    title: "Preview Transformed Data",
    description: "Review sample outputs to confirm accuracy",
  },
  {
    title: "Resolve Any Errors",
    description:
      "Fix mapping conflicts or transformation issues before proceeding",
  },
];

// Info Panels Component
const MappingInfoPanels = () => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      {/* Steps to Complete Panel */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Check className="h-5 w-5 text-green-600" />
          <h3 className="font-semibold text-green-800">
            Complete These Steps to Continue:
          </h3>
        </div>

        <div className="space-y-3 text-sm text-green-700">
          {steps.map((step, index) => (
            <div key={index} className="flex items-start space-x-2 pb-6">
              <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="h-3 w-3 text-white" />
              </div>
              <div>
                <strong>{step.title}</strong> - {step.description}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Column Mapping Matters Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <div className="flex items-center gap-2">
            <Info size={20} className="text-blue-500" />
            <h3 className="font-semibold text-blue-800">
              Why Column Mapping & Transformations Matter
            </h3>
          </div>
        </div>

        <div className="text-sm text-blue-700 mb-4">
          This step ensures your data migrates accurately and maintains quality
          standards.
        </div>

        <div className="space-y-3 text-sm text-blue-700">
          <div>
            <strong>What happens here:</strong>
            <ul className="list-disc list-inside mt-1 space-y-1 ml-4">
              <li>
                Your source data columns are automatically matched to target
                database fields
              </li>
              <li>
                Data transformations clean and standardize your information
              </li>
              <li>
                The system validates data quality and identifies potential
                issues
              </li>
            </ul>
          </div>

          <div>
            <strong>What happens if you skip proper mapping:</strong>
            <ul className="list-disc list-inside mt-1 space-y-1 ml-4">
              <li>
                Data may end up in wrong fields, causing confusion and errors
              </li>
              <li>
                Inconsistent formatting can break integrations and reports
              </li>
              <li>Missing required fields will cause the migration to fail</li>
              <li>
                Poor data quality affects system performance and user experience
              </li>
            </ul>
          </div>

          <div className="bg-blue-100 rounded-md p-3 mt-4">
            <strong>Why confidence levels matter:</strong>
            <ul className="list-disc list-inside mt-1 space-y-1 ml-2 text-xs">
              <li>
                <strong>High (Green):</strong> System is confident about the
                mapping - usually safe to proceed
              </li>
              <li>
                <strong>Medium (Yellow):</strong> Review recommended - may need
                adjustment
              </li>
              <li>
                <strong>Low (Red):</strong> Requires your attention - mapping
                may be incorrect
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Enhanced Column Mapping Table Component
const EnhancedColumnMappingTable = ({
  targetFields = [],
  sourceColumnOptions = [],
  onApplyTransformation, // ← ADD this line if missing
  sourceAssignments = {},
  onRemoveTransformation,
  onSourceColumnChange,
  summary,
  dataMigrationName,
}) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    transformation: null,
    targetField: null,
    fileData: null,
  });

  const handleOpenTransformationModal = (
    transformation,
    targetField,
    fileData
  ) => {
    setModalState({
      isOpen: true,
      transformation,
      targetField,
      fileData,
    });
  };

  const handleTransformationChange = useCallback(
    (targetField: string, newTransformation: string) => {
      // If "no_transformation" is selected, don't open modal
      if (newTransformation === "no_transformation") {
        // Find the field to get the current transformation order
        const fieldData = targetFields.find((f) => f.name === targetField);

        if (
          fieldData?.transformation &&
          fieldData.transformation !== "no_transformation"
        ) {
          onApplyTransformation?.(targetField, {
            targetField: toSnakeCase(targetField),
            transformation: {
              type: "no_transformation",
              order: 1,
              config: {},
            },
          });
          // Call parent's remove transformation handler
          if (onRemoveTransformation) {
            onRemoveTransformation(targetField, 1); // Assuming order is 1
          }
        }
        return;
      }

      // Find the target field data to get fileData
      const fieldData = targetFields.find((f) => f.name === targetField);

      // Open the modal with the selected transformation
      setModalState({
        isOpen: true,
        transformation: newTransformation,
        targetField: targetField,
        fileData: fieldData?.rawData || null,
      });
    },
    [targetFields, onRemoveTransformation]
  );
  const prepareFileDataForMerge = () => {
    const columnDataMap = {};

    // Iterate through all target fields from API
    Object.entries(targetFields).forEach(([_, field]) => {
      const sourceCol = field.sourceColumn;

      if (sourceCol && field.rawData) {
        // Store raw data by source column name
        columnDataMap[sourceCol] = field.rawData;
      }
    });

    // For columns not yet mapped, add empty arrays
    sourceColumnOptions.forEach((col) => {
      if (!columnDataMap[col]) {
        columnDataMap[col] = [];
      }
    });

    return columnDataMap;
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      transformation: null,
      targetField: null,
      fileData: null,
    });
  };

  const handleApplyTransformation = (transformationConfig) => {
    // Call parent handler
    if (onApplyTransformation) {
      onApplyTransformation(
        transformationConfig.targetField,
        transformationConfig
      );
    }

    handleCloseModal();
  };

  return (
    <div className="space-y-6">
      {/* Info Panels */}
      <MappingInfoPanels />

      <FileSummaryCard summary={summary} migrationName={dataMigrationName} />

      {/* Main Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2">
            <div className="p-1 bg-blue-100 rounded">
              <Settings className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Column Mapping & Transformations
            </h3>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Map your source columns to target fields and configure data
            transformations
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  Target Field
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  Transformation
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  Source Column
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  Confidence
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  File Data
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  Transformed Data
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  Sample Data
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {targetFields.map((field, index) => {
                return (
                  <MappingRow
                    key={field.name}
                    targetField={field.name}
                    isRequired={field.isRequired}
                    transformation={field.transformation}
                    sourceColumn={field.sourceColumn}
                    confidence={field.confidence}
                    fileData={field.rawData} // ← CHANGE from field.fileData
                    transformedData={field.transformedData}
                    sampleData={field.sampleData}
                    sourceColumnOptions={sourceColumnOptions}
                    sourceAssignments={sourceAssignments} // ← ADD
                    allowedTransformations={field.allowedTransformations} // ← ADD
                    hasErrors={field.hasErrors} // ← ADD
                    isLoadingPreview={field.isLoadingPreview} // ← ADD
                    onTransformationChange={handleTransformationChange}
                    onSourceColumnChange={onSourceColumnChange}
                    onOpenTransformationModal={handleOpenTransformationModal}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {modalState.isOpen && modalState.transformation === "split" && (
        <SplitTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          fileData={modalState.fileData}
          availableTargetFields={targetFields.map((f) => f.name)} // You may need to adjust this
          onApplyTransformation={handleApplyTransformation}
          sourceColumn={undefined}
        />
      )}
      {modalState.isOpen && modalState.transformation === "merge" && (
        <MergeTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          availableSourceColumns={sourceColumnOptions}
          fileData={prepareFileDataForMerge()}
          onApplyTransformation={handleApplyTransformation}
        />
      )}

      {modalState.isOpen && modalState.transformation === "add_prefix" && (
        <AddPrefixTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          fileData={
            modalState.fileData ? modalState.fileData.map((s) => s?.trim()) : []
          }
          onApplyTransformation={handleApplyTransformation}
        />
      )}

      {modalState.isOpen && modalState.transformation === "add_suffix" && (
        <AddSuffixTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          fileData={
            modalState.fileData ? modalState.fileData.map((s) => s?.trim()) : []
          }
          onApplyTransformation={handleApplyTransformation}
        />
      )}

      {modalState.isOpen &&
        modalState.transformation === "remove_substring" && (
          <RemoveSubstringTransformationDialog
            isOpen={modalState.isOpen}
            onClose={handleCloseModal}
            targetField={modalState.targetField}
            fileData={
              modalState.fileData
                ? modalState.fileData.map((s) => s.trim())
                : []
            }
            onApplyTransformation={handleApplyTransformation}
          />
        )}

      {modalState.isOpen && modalState.transformation === "uppercase" && (
        <UppercaseTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          fileData={
            modalState.fileData ? modalState.fileData.map((s) => s.trim()) : []
          }
          onApplyTransformation={handleApplyTransformation}
        />
      )}

      {modalState.isOpen && modalState.transformation === "lowercase" && (
        <LowercaseTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          fileData={
            modalState.fileData ? modalState.fileData.map((s) => s.trim()) : []
          }
          onApplyTransformation={handleApplyTransformation}
        />
      )}

      {modalState.isOpen && modalState.transformation === "title_case" && (
        <TitleCaseTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          fileData={
            modalState.fileData ? modalState.fileData.map((s) => s.trim()) : []
          }
          onApplyTransformation={handleApplyTransformation}
        />
      )}

      {modalState.isOpen && modalState.transformation === "trim_whitespace" && (
        <TrimWhitespaceTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          fileData={
            modalState.fileData ? modalState.fileData.map((s) => s.trim()) : []
          }
          onApplyTransformation={handleApplyTransformation}
        />
      )}

      {modalState.isOpen && modalState.transformation === "find_replace" && (
        <FindReplaceTransformationDialog
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          targetField={modalState.targetField}
          fileData={
            modalState.fileData ? modalState.fileData.map((s) => s.trim()) : []
          }
          onApplyTransformation={handleApplyTransformation}
        />
      )}
    </div>
  );
};

export default EnhancedColumnMappingTable;
